import React, { useEffect, useRef, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuList, Flex, Text, Divider, Box } from "@chakra-ui/react";
import { AppTooltip } from "./AppTooltip";
import { useNavigate } from "react-router-dom";
import { colors, lightColorWithDarkTheme } from "../constants/GlobalStyles";
import {
  IconShare,
  IconLightningBoltOutline,
  IconHistory,
  IconSettings,
  IconDocument,
  IconCheckmark,
  IconStats,
  IconShield,
  IconBackpack,
  IconSparkle,
  IconSupportBadge,
  IconTrophy,
} from "../icons";
import {
  copyTextToClipboard,
  encryptText,
  getAnimalCharacterWithAccessoryImage,
  trackEvent,
} from "../backend/utils/helper-methods";
import {
  JUNGLE_SUPPORT_UI_IS_ENABLED,
  ManagerSubscriptionAccount,
  UserSubscriptionPlan,
} from "../constants/GlobalTypesAndEnumsAndConstants";
import { getDisplayNameForSubscriptionPlan } from "./upsell/upsellDisplayNameHelpers";
import { useGlobalWebsiteStore, useSubscriptionUpsellStore } from "../zustand/globalStore";
import { useCustomerSupportChatStore } from "../zustand/useCustomerSupportChatStore";
import { useTranslate } from "@tolgee/react";
import { DarkModeMenuItem } from "./DarkModeMenuItem";
import dotsHorizontal from "@mdi/svg/svg/dots-horizontal.svg";
import { useTutorUiEnabled } from "../features/tutor/integration/tutorAvailability";
import { setSharerDeviceParameter, sharerDeviceQueryPair } from "../utils/sharer-device";

type ProfileButtonWithDropdownProps = {
  isProfileDropDownMenuOpen: boolean;
  setIsProfileDropDownMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: any;
  isMobile?: boolean;
  mobileTriggerProvidedByParent?: boolean;
  currentReviewDeck?: any;
  userSubscriptionPlan?: string;
  userNumOfMonthlyGenerationsLeft?: number;
  managerSubscriptionAccount?: string;
  isRunningAsMobileApp?: boolean;
  sendEventsToMobileApp?: (event: string) => void;
  hasUserGeneratedASetOfQuestions?: boolean | string;
  isCurrentlyReviewingADeck?: boolean;
};

const ProfileButtonWithDropdown: React.FC<ProfileButtonWithDropdownProps> = ({
  isProfileDropDownMenuOpen,
  setIsProfileDropDownMenuOpen,
  user,
  isMobile = false,
  mobileTriggerProvidedByParent = false,
  currentReviewDeck,
  userSubscriptionPlan = UserSubscriptionPlan.FREE,
  userNumOfMonthlyGenerationsLeft = 0,
  managerSubscriptionAccount,
  isRunningAsMobileApp = false,
  sendEventsToMobileApp,
  hasUserGeneratedASetOfQuestions = false,
  isCurrentlyReviewingADeck = false,
}) => {
  const navigate = useNavigate();
  const tutorUiEnabled = useTutorUiEnabled();
  const menuRef = useRef(null);
  const { t } = useTranslate();

  //zustand states
  const { isNavigationBarCollapsed } = useGlobalWebsiteStore((state) => state);
  const { showSubscriptionUpsellModal } = useSubscriptionUpsellStore((state) => state);

  // Local state
  const [didCopyJungleShareLink, setDidCopyJungleShareLink] = useState<boolean>(false);
  const [didCopyNavShareLink, setDidCopyNavShareLink] = useState<boolean>(false);

  // The bottom navigation mounts this menu already open, so Chakra's own "move focus into the menu when it
  // opens" never runs there, and a keyboard could open the menu but not reach its items. This does what
  // Chakra does after a press on its own button: focus goes to the menu, and the arrow keys take it from there.
  useEffect(() => {
    if (!mobileTriggerProvidedByParent || !isProfileDropDownMenuOpen) return;
    (menuRef.current as HTMLElement | null)?.focus({ preventScroll: true });
  }, [mobileTriggerProvidedByParent, isProfileDropDownMenuOpen]);

  // Render mobile menu button or desktop menu button
  const renderMenuButton = () => {
    if (isMobile) {
      return (
        <MenuButton
          aria-label={mobileTriggerProvidedByParent ? undefined : t("settings", "settings")}
          // The bottom navigation's own "more" button is the control. This empty button only anchors the
          // menu there, so it is kept away from the keyboard and from screen readers.
          aria-hidden={mobileTriggerProvidedByParent ? true : undefined}
          tabIndex={mobileTriggerProvidedByParent ? -1 : undefined}
          minW={mobileTriggerProvidedByParent ? undefined : "44px"}
          h={mobileTriggerProvidedByParent ? undefined : "44px"}
          color={colors.secondaryAction.default}
          onClick={(event) => {
            event.stopPropagation();
            setIsProfileDropDownMenuOpen((prev) => !prev);
          }}
          background="transparent"
          _hover={{ bg: "transparent" }}
          _expanded={{ bg: "transparent" }}
        >
          {/* The bottom navigation owns its trigger; standalone headers need their own. */}
          {!mobileTriggerProvidedByParent && (
            <Box
              as="span"
              aria-hidden="true"
              display="inline-block"
              boxSize="24px"
              bg="currentColor"
              sx={{
                mask: `url("${dotsHorizontal}") center / contain no-repeat`,
                WebkitMask: `url("${dotsHorizontal}") center / contain no-repeat`,
              }}
            />
          )}
        </MenuButton>
      );
    }

    if (isNavigationBarCollapsed) {
      // The icon rail ends with the learner's own avatar (their character), the gear only when none was chosen.
      const CollapsedCharacterImage = user?.gamification_properties?.character_with_accessory_selected
        ? getAnimalCharacterWithAccessoryImage(user.gamification_properties.character_with_accessory_selected) : null;
      return (
        <MenuButton
          aria-label={user?.name ? `${user.name}, ${t("settings", "settings")}` : t("settings", "settings")}
          onClick={(event) => {
            event.stopPropagation();
            setIsProfileDropDownMenuOpen((prev) => !prev);
          }}
          padding={CollapsedCharacterImage ? "4px" : "12px 24px"}
          borderRadius={CollapsedCharacterImage ? "50%" : "16px"}
          border={`1px solid ${colors.secondaryAction.light}`}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={CollapsedCharacterImage ? "48px" : "100%"}
          height={CollapsedCharacterImage ? "48px" : undefined}
          marginInline={CollapsedCharacterImage ? "auto" : undefined}
          _hover={{ borderColor: colors.secondaryAction.hover }}
        >
          {CollapsedCharacterImage
            ? <CollapsedCharacterImage height={38} width={38} style={{ borderRadius: "50%", objectFit: "cover", display: "block" }} />
            : <IconSettings color={colors.secondaryAction.default} />}
        </MenuButton>
      );
    }

    const CharacterProfileImage = getAnimalCharacterWithAccessoryImage(
      user?.gamification_properties?.character_with_accessory_selected
    );

    return (
      <Flex
        alignItems="center"
        gap="0"
        padding="6px 16px"
        borderRadius="16px"
        border={`1px solid ${colors.secondaryAction.light}`}
        width="100%"
      >
        <MenuButton
          onClick={(event) => {
            event.stopPropagation();
            setIsProfileDropDownMenuOpen((prev) => !prev);
          }}
          background="transparent"
          _hover={{ bg: "transparent" }}
          _expanded={{ bg: "transparent" }}
          padding="0"
          border="none"
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center" gap="12px">
            {user?.gamification_properties?.character_with_accessory_selected && (
              <CharacterProfileImage
                height={32}
                width={32}
                style={{
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            )}
            <Text fontFamily="Outfit" fontWeight="400" fontSize="14px" lineHeight="1.28" color={colors.text.body}>
              {user?.name}
            </Text>
          </Flex>
        </MenuButton>

        <Flex alignItems="center" gap="8px">
          <AppTooltip
            label={didCopyNavShareLink ? "link copied!" : "share Jungle"}
          >
            <Box
              as="button"
              aria-label={
                didCopyNavShareLink
                  ? t("link_copied", "link copied!")
                  : t("share_jungle_with_a_friend", "share Jungle with a friend")
              }
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDidCopyNavShareLink(true);
                copyTextToClipboard(`https://jungleai.com?r=${encryptText(user.id)}&${sharerDeviceQueryPair()}`);
                trackEvent(
                  "Referral Link Copied",
                  {
                    shareType: "Link copied",
                    location: "Navigation Bar Share Icon",
                  },
                  user,
                  currentReviewDeck
                );
              }}
              padding="6px"
              borderRadius="4px"
              _hover={{ cursor: "pointer" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="transparent"
              border="none"
            >
              {didCopyNavShareLink ? (
                <IconCheckmark color={colors.secondaryAction.default} width={20} height={20} />
              ) : (
                <IconShare color={colors.secondaryAction.default} width={20} height={20} />
              )}
            </Box>
          </AppTooltip>
          <AppTooltip
            label="settings"
          >
            <Box
              as="button"
              aria-label={t("settings", "settings")}
              onClick={(event) => {
                event.stopPropagation();
                setIsProfileDropDownMenuOpen((prev) => !prev);
              }}
              padding="6px"
              borderRadius="4px"
              _hover={{ cursor: "pointer" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="transparent"
              border="none"
            >
              <IconSettings color={colors.secondaryAction.default} />
            </Box>
          </AppTooltip>
        </Flex>
      </Flex>
    );
  };

  const handleShareLink = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setDidCopyJungleShareLink(true);

    let isReferralLinkOfTheDeck = false;
    let linkToCopy = `${window.location.host}?r=${encryptText(user.id)}&${sharerDeviceQueryPair()}`;

    if (currentReviewDeck && isCurrentlyReviewingADeck) {
      isReferralLinkOfTheDeck = true;
      let urlToShare = new URL(window.location.href);

      if (!urlToShare.searchParams.has("r")) {
        urlToShare.searchParams.set("r", encryptText(user.id));
      }

      setSharerDeviceParameter(urlToShare.searchParams);

      urlToShare.searchParams.set("utm_source", "friend_referral");
      linkToCopy = urlToShare.toString();
    }

    await copyTextToClipboard(linkToCopy);

    trackEvent(
      "Referral Link Copied",
      {
        location: "Sidebar drop down menu",
        isReferralLinkOfTheDeck,
      },
      user,
      currentReviewDeck
    );

    // Auto-close menu after copy on mobile
    if (isMobile) {
      setTimeout(() => {
        setIsProfileDropDownMenuOpen(false);
        setDidCopyJungleShareLink(false);
      }, 1500);
    }
  };

  const handleUpgradeSubscription = async () => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    showSubscriptionUpsellModal({
      subscriptionPlanToShow: UserSubscriptionPlan.SUPER_LEARNER,
      contextForShowingSubscriptionUpsell: "Sidebar drop down menu",
    });

    if (isMobile) {
      setIsProfileDropDownMenuOpen(false);
    }
  };

  const handleUpgradeFromProToElite = async () => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    // Elite-targeted upsell for existing Pro subscribers: skip the commit page
    // and open the plan picker directly (Pro card renders as "current plan").
    showSubscriptionUpsellModal({
      contextForShowingSubscriptionUpsell: "Sidebar drop down menu - upgrade from pro to elite",
      initialStep: "plan_picker",
      defaultHighlightedPlanOnStepTwo: UserSubscriptionPlan.ELITE,
    });

    if (isMobile) {
      setIsProfileDropDownMenuOpen(false);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsProfileDropDownMenuOpen(false);
  };

  // Actions to open the floating customer support chatbot panel. We subscribe
  // only to the action setters so toggling the panel never triggers a re-render
  // of the entire dropdown.
  const openCustomerSupportChatbotPanel = useCustomerSupportChatStore((state) => state.openPanel);
  const setCustomerSupportChatbotPanelView = useCustomerSupportChatStore((state) => state.setCurrentView);
  const handleOpenCustomerSupportChatbotFromProfileDropdown = () => {
    if (!JUNGLE_SUPPORT_UI_IS_ENABLED) {
      handleNavigation("/give-feedback");
      return;
    }
    setCustomerSupportChatbotPanelView("feedback_options");
    openCustomerSupportChatbotPanel();
    setIsProfileDropDownMenuOpen(false);
  };

  const menuItemStyles = {
    padding: "12px",
    fontFamily: "Outfit",
    fontSize: "16px",
    fontStyle: "normal",
    lineHeight: "24px",
    fontWeight: "400",
    color: colors.secondaryAction.default,
    _hover: { bg: colors.surface.focus },
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  // Common menu items
  return (
    <Menu
      isOpen={isProfileDropDownMenuOpen}
      // Navigation bars own outside-click and toggle handling for their triggers.
      onClose={isMobile && !mobileTriggerProvidedByParent ? () => setIsProfileDropDownMenuOpen(false) : undefined}
    >
      {renderMenuButton()}

      <MenuList
        ref={menuRef}
        padding={0}
        width="260px"
        border={`0.5px solid ${lightColorWithDarkTheme("#CCCCCC", colors.surface.border)}`}
        borderRadius="8px"
        boxShadow="0px 2px 20px rgba(0, 0, 0, 0.08)"
        zIndex={99999}
      >
        <MenuItem
          {...menuItemStyles}
          icon={<IconShare color={colors.secondaryAction.default} />}
          opacity={didCopyJungleShareLink ? 0.4 : 1}
          borderTopRadius="8px"
          onClick={handleShareLink}
          // Keep the confirmation visible until handleShareLink's delayed close.
          closeOnSelect={false}
        >
          {!didCopyJungleShareLink
            ? isCurrentlyReviewingADeck
              ? t("share_these_questions_with_a_friend", "share these questions with a friend")
              : t("share_jungle_with_a_friend", "share Jungle with a friend")
            : t("link_copied", "link copied!")}
        </MenuItem>
        <Divider orientation="horizontal" />

        {isMobile && tutorUiEnabled && <>
          <MenuItem {...menuItemStyles} icon={<IconTrophy width={14} height={14} color={colors.secondaryAction.default} />}
            onClick={() => handleNavigation("/leaderboard")}>
            {t("phone_navigation_league", "league")}
          </MenuItem>
          <Divider orientation="horizontal" />
        </>}

        {!hasUserGeneratedASetOfQuestions && window.location.pathname !== "/" && (
          <>
            <MenuItem
              {...menuItemStyles}
              icon={<IconDocument color={colors.secondaryAction.default} width={14} />}
              gap="10px"
              onClick={() => navigate("/upload-document")}
            >
              {t("generate_questions_from_a_file", "generate questions from a file")}
            </MenuItem>
            <Divider orientation="horizontal" />
          </>
        )}

        {userSubscriptionPlan !== UserSubscriptionPlan.SUPER_LEARNER &&
          userSubscriptionPlan !== UserSubscriptionPlan.ELITE &&
          !(
            !isRunningAsMobileApp &&
            (managerSubscriptionAccount === ManagerSubscriptionAccount.Apple ||
              managerSubscriptionAccount === ManagerSubscriptionAccount.Google)
          ) && (
            <>
              <MenuItem
                {...menuItemStyles}
                icon={<IconLightningBoltOutline color={colors.secondaryAction.default} />}
                onClick={handleUpgradeSubscription}
              >
                {t("upgrade_to_premium", "upgrade to premium")}
              </MenuItem>
              <Divider orientation="horizontal" />
            </>
          )}

        {/* Pro subscribers get an Elite-targeted upgrade entry instead of the
            generic one. Hidden on web for Apple/Google-managed subs (they can't
            change plans outside their store). */}
        {userSubscriptionPlan === UserSubscriptionPlan.SUPER_LEARNER &&
          !(
            !isRunningAsMobileApp &&
            (managerSubscriptionAccount === ManagerSubscriptionAccount.Apple ||
              managerSubscriptionAccount === ManagerSubscriptionAccount.Google)
          ) && (
            <>
              <MenuItem
                {...menuItemStyles}
                icon={<IconLightningBoltOutline color={colors.secondaryAction.default} />}
                onClick={handleUpgradeFromProToElite}
              >
                {t("upgrade_from_pro_to_plan_name", "upgrade from pro to {planName}", {
                  // Elite renders under the student's discipline (medical/dental/…)
                  planName: getDisplayNameForSubscriptionPlan(
                    UserSubscriptionPlan.ELITE,
                    user?.student_type ?? null
                  ).toLowerCase(),
                })}
              </MenuItem>
              <Divider orientation="horizontal" />
            </>
          )}

        <MenuItem
          {...menuItemStyles}
          icon={<IconHistory color={colors.secondaryAction.default} />}
          onClick={() => handleNavigation("/generation-history")}
        >
          {t("generation_history", "generation history")}
        </MenuItem>
        <Divider orientation="horizontal" />

        <MenuItem
          {...menuItemStyles}
          icon={<IconStats color={colors.secondaryAction.default} />}
          onClick={() => handleNavigation("/streak-and-stats")}
        >
          {t("view_stats", "view stats")}
        </MenuItem>
        <Divider orientation="horizontal" />

        {/* Desktop only - mobile users see the gratitude wall through the daily review flow.
            Conditionally rendered (not CSS-hidden) so the hidden item is never registered
            in Chakra's menu keyboard navigation on mobile. */}
        {!isMobile && (
          <>
            <MenuItem
              {...menuItemStyles}
              icon={<IconSparkle color={colors.secondaryAction.default} width={20} height={20} />}
              onClick={() => handleNavigation("/gratitude-wall")}
            >
              {t("view_todays_gratitude_wall", "view today's gratitude wall")}
            </MenuItem>
            <Divider orientation="horizontal" />
          </>
        )}

        {user?.role === "admin" && (
          <>
            <MenuItem
              {...menuItemStyles}
              icon={<IconShield color={colors.secondaryAction.default} width={22} height={22} />}
              onClick={() => handleNavigation("/admin")}
            >
              {t("admin_dashboard", "admin dashboard")}
            </MenuItem>
            <Divider orientation="horizontal" />
          </>
        )}

        <MenuItem
          {...menuItemStyles}
          icon={<IconSupportBadge color={colors.secondaryAction.default} width={20} height={20} />}
          onClick={handleOpenCustomerSupportChatbotFromProfileDropdown}
        >
          {t("give_feedback", "get help / give feedback")}
        </MenuItem>
        {/* Unconditional: on desktop the backpack item below is hidden, so this is
            the only divider separating "give feedback" from "settings". */}
        <Divider orientation="horizontal" />
        <MenuItem
          {...menuItemStyles}
          // AFTER the spread — menuItemStyles carries display:"flex" which would
          // otherwise override the responsive hide (desktop has backpack in the nav bar)
          display={{ base: "flex", md: "none" }}
          icon={<IconBackpack color={colors.secondaryAction.default} width={20} height={20} />}
          onClick={async () => {
            handleNavigation("/backpack");
          }}
          justifyContent="center"
          alignItems="center"
        >
          {t("backpack", "backpack")}
        </MenuItem>
        <Divider orientation="horizontal" display={{ base: "block", md: "none" }} />
        <DarkModeMenuItem {...menuItemStyles} />
        <Divider orientation="horizontal" />
        <MenuItem
          {...menuItemStyles}
          borderBottomRadius="8px"
          icon={<IconSettings color={colors.secondaryAction.default} />}
          onClick={() => handleNavigation("/settings")}
        >
          {t("settings", "settings")}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default ProfileButtonWithDropdown;
